import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Users, Activity, Clock, Trophy, ChevronRight, Search, Eye, Globe, TrendingUp, BarChart3, Star, Trash2, UserPlus, Check, X, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/utils/constants';
import { useAuth } from '@/contexts/AuthContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import AdminReviewsTab from '@/components/admin/AdminReviewsTab';

// Split "Prénom Nom..." into { firstName, lastName } by taking the LAST word
// as the family name. Keeps composed last names like "Van der Berg" if the
// user typed them as a single word, otherwise falls back to the last token.
const splitName = (fullName) => {
  const parts = (fullName || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: '', lastName: parts[0] };
  const lastName = parts[parts.length - 1];
  const firstName = parts.slice(0, -1).join(' ');
  return { firstName, lastName };
};

// Display helper: show "Nom Prénom" (last name first) as requested by admin.
const formatNameLastFirst = (fullName) => {
  const { firstName, lastName } = splitName(fullName);
  if (!firstName && !lastName) return '(sans nom)';
  if (!firstName) return lastName;
  return `${lastName} ${firstName}`;
};

// Comparator for alphabetical sort by family name (locale-aware, FR).
const compareByLastName = (a, b) => {
  const la = splitName(a?.name).lastName.toLowerCase();
  const lb = splitName(b?.name).lastName.toLowerCase();
  return la.localeCompare(lb, 'fr', { sensitivity: 'base' });
};

const AdminPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [globalStats, setGlobalStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [isMounted, setIsMounted] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, name } of user to delete
  const [deleting, setDeleting] = useState(false);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [pendingBusy, setPendingBusy] = useState({}); // { [user_id]: 'approve'|'reject' }
  const [activityOverview, setActivityOverview] = useState(null);
  const [overviewFilter, setOverviewFilter] = useState('all'); // 'all' | 'active' | 'warm' | 'cool' | 'cold' | 'never'
  const [overviewSearch, setOverviewSearch] = useState('');
  const [backfillingLogins, setBackfillingLogins] = useState(false);

  // Mark component as mounted for client-side only rendering (fixes recharts in production)
  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (user && !user.is_admin) {
      navigate('/app');
      toast.error("Accès réservé aux administrateurs");
      return;
    }
    
    const loadData = async () => {
      try {
        const [statsRes, usersRes, analyticsRes, pendingRes, overviewRes] = await Promise.all([
          axios.get(`${API}/admin/stats`),
          axios.get(`${API}/admin/users`),
          axios.get(`${API}/admin/analytics?days=30`),
          axios.get(`${API}/admin/pending-users`),
          axios.get(`${API}/admin/users/activity-overview`).catch(() => ({ data: null })),
        ]);
        setGlobalStats(statsRes.data);
        setUsers(usersRes.data);
        setAnalytics(analyticsRes.data);
        setPendingUsers(pendingRes.data || []);
        setActivityOverview(overviewRes.data || null);
      } catch (err) {
        if (err.response?.status === 403) {
          toast.error("Accès réservé aux administrateurs");
          navigate('/app');
        } else {
          toast.error("Erreur lors du chargement des données");
        }
      } finally {
        setLoading(false);
      }
    };
    
    if (user?.is_admin) {
      loadData();
    }
  }, [user, navigate]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const filteredUsers = users.filter(u => 
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  ).sort(compareByLastName);

  const handleBackfillLogins = async () => {
    if (backfillingLogins) return;
    if (!window.confirm(
      "Créer un événement 'login' rétroactif pour chaque patient qui a une activité (challenges, sessions, check-ins) mais aucun événement de connexion ? Cette action est idempotente : les patients déjà comptés seront ignorés."
    )) return;
    setBackfillingLogins(true);
    try {
      const { data } = await axios.post(`${API}/admin/analytics/backfill-logins`);
      toast.success(`${data.inserted} connexion(s) rétroactive(s) créée(s). ${data.skipped_already_had_login} déjà comptés · ${data.skipped_no_activity} sans activité.`);
      // Refresh the activity overview
      const overviewRes = await axios.get(`${API}/admin/users/activity-overview`);
      setActivityOverview(overviewRes.data);
    } catch (err) {
      toast.error("Erreur lors de la rétro-migration : " + (err.response?.data?.detail || err.message));
    } finally {
      setBackfillingLogins(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteConfirm) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/users/${deleteConfirm.id}`);
      setUsers(users.filter(u => u.id !== deleteConfirm.id));
      toast.success(`Utilisateur "${deleteConfirm.name}" supprimé avec succès`);
      setDeleteConfirm(null);
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setDeleting(false);
    }
  };

  const handleApprovePending = async (u) => {
    setPendingBusy((b) => ({ ...b, [u.id]: 'approve' }));
    try {
      await axios.post(`${API}/admin/users/${u.id}/approve`);
      setPendingUsers((p) => p.filter((x) => x.id !== u.id));
      toast.success(`${u.name} a été approuvé. Un email de bienvenue vient de partir.`);
      // Refresh the general users list so the new active user appears there
      try {
        const r = await axios.get(`${API}/admin/users`);
        setUsers(r.data);
      } catch { /* non-blocking */ }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible d'approuver ce compte");
    } finally {
      setPendingBusy((b) => { const c = { ...b }; delete c[u.id]; return c; });
    }
  };

  const handleRejectPending = async (u) => {
    if (!window.confirm(`Refuser et supprimer la demande de ${u.name} (${u.email}) ?`)) return;
    setPendingBusy((b) => ({ ...b, [u.id]: 'reject' }));
    try {
      await axios.post(`${API}/admin/users/${u.id}/reject`);
      setPendingUsers((p) => p.filter((x) => x.id !== u.id));
      toast.success(`Demande de ${u.name} refusée.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible de refuser ce compte");
    } finally {
      setPendingBusy((b) => { const c = { ...b }; delete c[u.id]; return c; });
    }
  };

  const getPageName = (path) => {
    const names = {
      '/': 'Accueil',
      '/hyperventilation': 'Hyperventilation',
      '/respiration-dysfonctionnelle': 'Respiration Dysfonctionnelle',
      '/covid-long': 'COVID Long',
      '/asthme': 'Asthme',
      '/methode-buteyko': 'Méthode Buteyko',
      '/coherence-cardiaque': 'Cohérence Cardiaque',
      '/nerf-vague': 'Nerf Vague',
      '/documents': 'Documents',
      '/breathing-app': 'Application',
      '/app': 'Exercices',
      '/login': 'Connexion',
    };
    return names[path] || path;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-teal-50 flex items-center justify-center">
        <div className="text-stone-400">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-indigo-50 pb-24">
      <div className="max-w-4xl mx-auto px-6 pt-8">
        <h1 className="text-2xl font-bold text-stone-800 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Administration
        </h1>
        <p className="text-stone-500 text-sm mb-6">Vue d&apos;ensemble de la plateforme</p>

        {/* Recent sign-ups (< 24h) — fallback UI when the notification email fails */}
        {(() => {
          const cutoff = Date.now() - 24 * 60 * 60 * 1000;
          const recent = (users || []).filter(u => {
            const t = new Date(u.created_at).getTime();
            return !isNaN(t) && t >= cutoff;
          }).sort((a,b) => new Date(b.created_at) - new Date(a.created_at));
          if (!recent.length) return null;
          return (
            <div
              className="mb-6 rounded-2xl p-4 border"
              style={{ backgroundColor: '#F0F9FF', borderColor: '#BAE6FD' }}
              data-testid="admin-recent-signups"
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold" style={{ backgroundColor: '#0284C7', color: 'white' }}>{recent.length}</span>
                <p className="text-sm font-semibold" style={{ color: '#0369A1' }}>
                  Nouveau{recent.length > 1 ? 'x' : ''} compte{recent.length > 1 ? 's' : ''} (dernières 24h)
                </p>
              </div>
              <div className="space-y-2">
                {recent.slice(0, 8).map(u => (
                  <div key={u.id} className="flex items-center justify-between gap-3 text-sm bg-white rounded-lg px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate" style={{ color: '#0F172A' }}>{u.name}</p>
                      <a href={`mailto:${u.email}`} className="text-xs truncate block" style={{ color: '#0284C7' }}>{u.email}</a>
                    </div>
                    <span className="text-xs" style={{ color: '#64748B' }}>{formatDate(u.created_at)}</span>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/admin/users/${u.id}/resend-notification`);
                          toast.success("Notification renvoyée");
                        } catch (e) {
                          toast.error("Échec envoi : " + (e.response?.data?.detail || e.message));
                        }
                      }}
                      className="text-xs px-3 py-1 rounded-full border transition-colors hover:bg-sky-50"
                      style={{ borderColor: '#0284C7', color: '#0284C7' }}
                      data-testid={`resend-notif-${u.id}`}
                    >
                      Renvoyer notif
                    </button>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full bg-white/70 backdrop-blur mb-6">
            <TabsTrigger value="overview" className="flex-1">Vue générale</TabsTrigger>
            <TabsTrigger value="activity-overview" className="flex-1">Activité patients</TabsTrigger>
            <TabsTrigger value="analytics" className="flex-1">Visiteurs</TabsTrigger>
            <TabsTrigger value="users" className="flex-1">Utilisateurs</TabsTrigger>
            <TabsTrigger value="reviews" className="flex-1" data-testid="admin-reviews-tab">
              <Star size={14} className="mr-1" />
              Avis
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            {/* Diagnostic email button (admin only) */}
            <Card className="mb-4" data-testid="diag-email-card">
              <CardContent className="py-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm text-stone-700">
                  <p className="font-semibold">Diagnostic email</p>
                  <p className="text-xs text-stone-500">Envoie un email de test aux adresses admin et affiche la réponse Resend brute.</p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const { data } = await axios.post(`${API}/admin/send-test-email`);
                      if (data.ok) {
                        toast.success(`Envoyé à ${data.recipients.join(', ')} — id ${data.resend?.id || 'ok'}`);
                      } else {
                        toast.error(`Échec Resend : ${data.error}`, { duration: 12000 });
                      }
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Erreur réseau');
                    }
                  }}
                  className="px-4 py-2 rounded-full bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold transition"
                  data-testid="diag-send-test-email"
                >
                  Envoyer un email de test
                </button>
              </CardContent>
            </Card>

            {/* Pending accounts */}
            {pendingUsers.length > 0 && (
              <Card className="mb-6 border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50" data-testid="pending-users-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2 text-amber-900">
                    <UserPlus size={18} /> Nouvelles demandes d&apos;accès
                    <span className="ml-1 text-xs font-semibold bg-amber-500 text-white rounded-full px-2 py-0.5" data-testid="pending-users-count">{pendingUsers.length}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <ul className="divide-y divide-amber-200/70">
                    {pendingUsers.map((u) => {
                      const busy = pendingBusy[u.id];
                      return (
                        <li key={u.id} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`pending-user-${u.id}`}>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-stone-800 truncate">{u.name}</p>
                            <p className="text-xs text-stone-500 truncate">{u.email}</p>
                            <p className="text-[11px] text-stone-400 mt-0.5">Inscription : {formatDate(u.created_at)}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleApprovePending(u)}
                              disabled={!!busy}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 transition disabled:opacity-60"
                              data-testid={`pending-approve-${u.id}`}
                            >
                              <Check size={13} /> {busy === 'approve' ? 'En cours…' : 'Approuver'}
                            </button>
                            <button
                              onClick={() => handleRejectPending(u)}
                              disabled={!!busy}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-white bg-rose-600 hover:bg-rose-700 transition disabled:opacity-60"
                              data-testid={`pending-reject-${u.id}`}
                            >
                              <X size={13} /> {busy === 'reject' ? 'En cours…' : 'Refuser'}
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </CardContent>
              </Card>
            )}

            {globalStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 border-0">
                  <CardContent className="pt-4 text-center">
                    <Users className="mx-auto mb-2 text-white/80" size={24} />
                    <p className="text-2xl font-bold text-white">{globalStats.total_users}</p>
                    <p className="text-xs text-indigo-100">Utilisateurs</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-teal-500 to-teal-600 border-0">
                  <CardContent className="pt-4 text-center">
                    <Activity className="mx-auto mb-2 text-white/80" size={24} />
                    <p className="text-2xl font-bold text-white">{globalStats.total_sessions}</p>
                    <p className="text-xs text-teal-100">Sessions</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-amber-500 to-amber-600 border-0">
                  <CardContent className="pt-4 text-center">
                    <Clock className="mx-auto mb-2 text-white/80" size={24} />
                    <p className="text-2xl font-bold text-white">{globalStats.total_minutes}</p>
                    <p className="text-xs text-amber-100">Minutes</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-rose-500 to-rose-600 border-0">
                  <CardContent className="pt-4 text-center">
                    <Trophy className="mx-auto mb-2 text-white/80" size={24} />
                    <p className="text-2xl font-bold text-white">{globalStats.total_challenges}</p>
                    <p className="text-xs text-rose-100">Défis</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Analytics Summary */}
            {analytics && (
              <div className="grid grid-cols-2 gap-3 mb-6">
                <Card className="bg-gradient-to-br from-purple-500 to-violet-600 border-0">
                  <CardContent className="pt-4 text-center">
                    <Globe className="mx-auto mb-2 text-white/80" size={24} />
                    <p className="text-2xl font-bold text-white">{analytics.all_time_visits}</p>
                    <p className="text-xs text-purple-100">Visites totales</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-cyan-500 to-blue-600 border-0">
                  <CardContent className="pt-4 text-center">
                    <Eye className="mx-auto mb-2 text-white/80" size={24} />
                    <p className="text-2xl font-bold text-white">{analytics.all_time_unique_visitors}</p>
                    <p className="text-xs text-cyan-100">Visiteurs uniques</p>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Activity Overview Tab — birds'-eye view of every patient */}
          <TabsContent value="activity-overview">
            {!activityOverview ? (
              <p className="text-sm text-slate-500 text-center py-8">Chargement…</p>
            ) : (() => {
              const STATUS_META = {
                active: { label: 'Actif (≤ 7 j)', bg: 'bg-emerald-500', ring: 'ring-emerald-200', pill: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
                warm:   { label: 'Récent (8-14 j)', bg: 'bg-lime-500', ring: 'ring-lime-200', pill: 'bg-lime-100 text-lime-800 border-lime-200' },
                cool:   { label: 'Fraîchi (15-30 j)', bg: 'bg-amber-500', ring: 'ring-amber-200', pill: 'bg-amber-100 text-amber-800 border-amber-200' },
                cold:   { label: 'Froid (>30 j)', bg: 'bg-rose-500', ring: 'ring-rose-200', pill: 'bg-rose-100 text-rose-800 border-rose-200' },
                never:  { label: 'Jamais connecté', bg: 'bg-slate-500', ring: 'ring-slate-200', pill: 'bg-slate-100 text-slate-800 border-slate-200' },
              };
              const { summary, users: allUsers } = activityOverview;
              const filtered = allUsers.filter((u) => {
                if (overviewFilter !== 'all' && u.status !== overviewFilter) return false;
                if (overviewSearch) {
                  const q = overviewSearch.toLowerCase();
                  if (!(u.name || '').toLowerCase().includes(q) && !(u.email || '').toLowerCase().includes(q)) return false;
                }
                return true;
              }).sort(compareByLastName);
              return (
                <div className="space-y-4">
                  {/* KPI summary */}
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                    {['active', 'warm', 'cool', 'cold', 'never'].map((k) => {
                      const meta = STATUS_META[k];
                      const count = summary[k] || 0;
                      return (
                        <button
                          key={k}
                          type="button"
                          onClick={() => setOverviewFilter(overviewFilter === k ? 'all' : k)}
                          className={`text-left rounded-xl p-3 border transition ${overviewFilter === k ? 'ring-2 ' + meta.ring : ''} ${meta.pill}`}
                          data-testid={`activity-filter-${k}`}
                        >
                          <p className="text-[10px] uppercase tracking-wide font-bold">{meta.label}</p>
                          <p className="text-2xl font-bold mt-1">{count}</p>
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <p className="text-xs text-slate-600">
                      {filtered.length} / {summary.total_users} patient{summary.total_users > 1 ? 's' : ''} affichés
                      {overviewFilter !== 'all' && (
                        <button
                          type="button"
                          onClick={() => setOverviewFilter('all')}
                          className="ml-2 text-sky-600 hover:text-sky-800 underline"
                        >
                          Retirer le filtre
                        </button>
                      )}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={handleBackfillLogins}
                        disabled={backfillingLogins}
                        className="text-xs font-semibold px-3 py-1.5 rounded-full border border-sky-300 bg-sky-50 text-sky-800 hover:bg-sky-100 disabled:opacity-60 disabled:cursor-not-allowed"
                        data-testid="backfill-logins-btn"
                        title="Créer un événement 'login' rétroactif pour chaque patient ayant une activité mais aucune connexion enregistrée"
                      >
                        {backfillingLogins ? 'Migration…' : 'Rétro-migrer les connexions'}
                      </button>
                      <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                          type="search"
                          value={overviewSearch}
                          onChange={(e) => setOverviewSearch(e.target.value)}
                          placeholder="Rechercher par nom / email"
                          className="pl-9 pr-3 py-1.5 rounded-full border border-slate-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-400 w-64"
                          data-testid="activity-overview-search"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Table */}
                  <Card className="bg-white border-slate-200">
                    <CardContent className="p-0">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 border-b border-slate-200">
                            <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                              <th className="px-3 py-2 font-semibold">Patient</th>
                              <th className="px-3 py-2 font-semibold hidden sm:table-cell">Dernière connexion</th>
                              <th className="px-3 py-2 font-semibold text-right">Depuis</th>
                              <th className="px-3 py-2 font-semibold text-right hidden md:table-cell">Ouvertures</th>
                              <th className="px-3 py-2 font-semibold text-right hidden md:table-cell">Pages 30 j</th>
                              <th className="px-3 py-2" />
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {filtered.length === 0 ? (
                              <tr><td colSpan={6} className="text-center py-6 text-slate-500 text-sm">Aucun patient dans cette catégorie.</td></tr>
                            ) : filtered.map((u) => {
                              const meta = STATUS_META[u.status] || STATUS_META.never;
                              return (
                                <tr key={u.id} className="hover:bg-slate-50/70 transition" data-testid={`activity-row-${u.id}`}>
                                  <td className="px-3 py-3">
                                    <div className="flex items-center gap-2">
                                      <span className={`w-2.5 h-2.5 rounded-full ${meta.bg} flex-shrink-0`} title={meta.label} />
                                      <div className="min-w-0">
                                        <p className="font-semibold text-slate-800 truncate">{formatNameLastFirst(u.name)}</p>
                                        <p className="text-[11px] text-slate-500 truncate">{u.email}</p>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-3 py-3 text-slate-600 hidden sm:table-cell whitespace-nowrap">
                                    {u.last_login
                                      ? new Date(u.last_login).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) + ' · ' + new Date(u.last_login).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                                      : <span className="italic text-slate-400">jamais</span>}
                                  </td>
                                  <td className="px-3 py-3 text-right whitespace-nowrap">
                                    {u.days_since_last_login === null
                                      ? <span className="text-slate-400">—</span>
                                      : <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold border ${meta.pill}`}>{u.days_since_last_login} j</span>}
                                  </td>
                                  <td className="px-3 py-3 text-right text-slate-700 font-mono hidden md:table-cell">{u.total_logins}</td>
                                  <td className="px-3 py-3 text-right text-slate-700 font-mono hidden md:table-cell">{u.page_views_30d}</td>
                                  <td className="px-3 py-3 text-right">
                                    <button
                                      type="button"
                                      onClick={() => navigate(`/admin/user/${u.id}`)}
                                      className="inline-flex items-center gap-1 text-xs font-semibold text-sky-700 hover:text-sky-900"
                                      data-testid={`activity-open-${u.id}`}
                                    >
                                      Détails <ChevronRight size={12} />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>

                  <p className="text-[11px] text-slate-400 text-center">
                    Généré le {new Date(summary.generated_at).toLocaleString('fr-FR')}
                  </p>
                </div>
              );
            })()}
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            {analytics && (
              <div className="space-y-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Card className="bg-white/70 backdrop-blur border-stone-200">
                    <CardContent className="pt-4 text-center">
                      <p className="text-2xl font-bold text-purple-600">{analytics.all_time_visits}</p>
                      <p className="text-xs text-stone-500">Visites totales</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-white/70 backdrop-blur border-stone-200">
                    <CardContent className="pt-4 text-center">
                      <p className="text-2xl font-bold text-cyan-600">{analytics.all_time_unique_visitors}</p>
                      <p className="text-xs text-stone-500">Visiteurs uniques</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-white/70 backdrop-blur border-stone-200">
                    <CardContent className="pt-4 text-center">
                      <p className="text-2xl font-bold text-teal-600">{analytics.total_visits}</p>
                      <p className="text-xs text-stone-500">30 derniers jours</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-white/70 backdrop-blur border-stone-200">
                    <CardContent className="pt-4 text-center">
                      <p className="text-2xl font-bold text-indigo-600">{analytics.unique_visitors}</p>
                      <p className="text-xs text-stone-500">Uniques (30j)</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Daily Visits Chart */}
                {analytics.daily_visits && analytics.daily_visits.length > 0 && (
                  <Card className="bg-white/70 backdrop-blur border-stone-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base font-semibold text-stone-800 flex items-center gap-2">
                        <BarChart3 size={18} className="text-teal-600" />
                        Visites des 7 derniers jours
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {isMounted ? (
                        <div className="h-48">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={analytics.daily_visits}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis 
                                dataKey="date" 
                                tick={{ fontSize: 10, fill: '#64748b' }}
                                tickFormatter={(val) => {
                                  const d = new Date(val);
                                  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
                                }}
                              />
                              <YAxis tick={{ fontSize: 10, fill: '#64748b' }} />
                              <Tooltip 
                                contentStyle={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                                labelFormatter={(val) => new Date(val).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}
                              />
                              <Bar dataKey="visits" fill="#0d9488" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      ) : (
                        <div className="h-48 flex items-center justify-center text-stone-400">Chargement du graphique...</div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Top Pages */}
                <Card className="bg-white/70 backdrop-blur border-stone-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold text-stone-800 flex items-center gap-2">
                      <TrendingUp size={18} className="text-purple-600" />
                      Pages les plus visitées
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {analytics.top_pages && analytics.top_pages.length > 0 ? (
                      <div className="space-y-2">
                        {analytics.top_pages.map((page, idx) => (
                          <div key={page.path} className="flex items-center justify-between p-2 bg-stone-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <span className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-xs font-bold">
                                {idx + 1}
                              </span>
                              <span className="text-sm text-stone-700">{getPageName(page.path)}</span>
                            </div>
                            <span className="text-sm font-semibold text-purple-600">{page.visits} visites</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-stone-400 py-4">Aucune donnée disponible</p>
                    )}
                  </CardContent>
                </Card>

                {/* Recent Visits */}
                <Card className="bg-white/70 backdrop-blur border-stone-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold text-stone-800 flex items-center gap-2">
                      <Eye size={18} className="text-cyan-600" />
                      Visites récentes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {analytics.recent_visits && analytics.recent_visits.length > 0 ? (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {analytics.recent_visits.map((visit, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-stone-50 rounded-lg text-sm">
                            <div>
                              <span className="text-stone-700">{getPageName(visit.page_path)}</span>
                              <span className="text-stone-400 ml-2 text-xs">#{visit.visitor_id.slice(0, 6)}</span>
                            </div>
                            <span className="text-xs text-stone-400">
                              {formatDate(visit.timestamp)} {formatTime(visit.timestamp)}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-stone-400 py-4">Aucune visite récente</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="bg-white/70 backdrop-blur border-stone-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold text-stone-800">
                  Utilisateurs ({users.length})
                </CardTitle>
                <div className="relative mt-3">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-400" size={18} />
                  <input
                    type="text"
                    placeholder="Rechercher par nom ou email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-stone-50 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-2">
                  {filteredUsers.length === 0 ? (
                    <p className="text-center text-stone-400 py-4">Aucun utilisateur trouvé</p>
                  ) : (
                    filteredUsers.map((u) => (
                      <div
                        key={u.id}
                        className="flex items-center justify-between p-3 bg-stone-50 hover:bg-indigo-50 rounded-lg transition-colors group"
                      >
                        <button
                          onClick={() => navigate(`/admin/user/${u.id}`)}
                          className="flex-1 flex items-center text-left"
                        >
                          <div className="flex-1">
                            <p className="font-medium text-stone-700 group-hover:text-indigo-700">{formatNameLastFirst(u.name)}</p>
                            <p className="text-xs text-stone-400">{u.email}</p>
                            <p className="text-xs text-stone-400 mt-1">Inscrit le {formatDate(u.created_at)}</p>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-sm font-semibold text-teal-600">{u.stats.total_sessions} sessions</p>
                              <p className="text-xs text-stone-400">{u.stats.total_minutes} min • {u.stats.total_challenges} défis</p>
                            </div>
                            <ChevronRight className="text-stone-300 group-hover:text-indigo-500 transition-colors" size={20} />
                          </div>
                        </button>
                        {u.id !== user?.id && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteConfirm({ id: u.id, name: u.name, email: u.email });
                            }}
                            className="ml-3 p-2 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Supprimer cet utilisateur"
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                        {/* Admin shortcut: open the weekly-program editor
                            prefilled with THIS patient's data — no login
                            juggling needed. */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/users/${u.id}/weekly-program`);
                          }}
                          className="ml-1 p-2 text-stone-400 hover:text-sky-600 hover:bg-sky-50 rounded-lg transition-colors"
                          title="Éditer le programme hebdo"
                          data-testid={`admin-open-weekly-${u.id}`}
                        >
                          <ClipboardList size={18} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews">
            <AdminReviewsTab />
          </TabsContent>
        </Tabs>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={() => !deleting && setDeleteConfirm(null)}
        >
          <div 
            className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="text-red-600" size={24} />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-stone-800">Supprimer l&apos;utilisateur ?</h3>
                <p className="text-sm text-stone-500">Cette action est irréversible</p>
              </div>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-stone-700">
                Vous êtes sur le point de supprimer <strong>{deleteConfirm.name}</strong> ({deleteConfirm.email}).
              </p>
              <p className="text-sm text-red-600 mt-2 font-medium">
                Toutes les données associées seront supprimées : sessions, défis, avis, patterns personnalisés.
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="flex-1 px-4 py-3 bg-stone-100 text-stone-700 rounded-xl font-medium hover:bg-stone-200 transition-colors disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteUser}
                disabled={deleting}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Suppression...
                  </>
                ) : (
                  <>
                    <Trash2 size={18} />
                    Supprimer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
